import type React from 'react';
import type { EmphasisDotsStyleType, RubyDisplayType } from '../../hooks/useReaderSettings';
import { AlignJustifyIcon, AlignRightIcon, CheckIcon, TypeIcon } from '../Icons';
import type { LineHeightPreset, MarginPreset, NovelLayout, NovelTheme } from '../ReaderSettings';

export interface NovelSettingsTabProps {
  layout: NovelLayout;
  setLayout: (layout: NovelLayout) => void;
  fontSize: number;
  setFontSize: (size: number) => void;
  lineHeight: LineHeightPreset;
  setLineHeight: (lh: LineHeightPreset) => void;
  fontFamily: 'serif' | 'sans';
  setFontFamily: (font: 'serif' | 'sans') => void;
  margin: MarginPreset;
  setMargin: (m: MarginPreset) => void;
  bookModeEnabled: boolean;
  setBookModeEnabled: (enabled: boolean) => void;
  currentTheme: NovelTheme;
  setTheme: (theme: NovelTheme) => void;
  focusModeEnabled: boolean;
  setFocusModeEnabled: (v: boolean) => void;
  focusLineCount: number;
  setFocusLineCount: (v: number) => void;
  rubyDisplay: RubyDisplayType;
  setRubyDisplay: (display: RubyDisplayType) => void;
  tateChuYokoEnabled: boolean;
  setTateChuYokoEnabled: (v: boolean) => void;
  isDark: boolean;
  emphasisDotsStyle: EmphasisDotsStyleType;
  setEmphasisDotsStyle: (style: EmphasisDotsStyleType) => void;
}

export const NovelSettingsTab: React.FC<NovelSettingsTabProps> = ({
  layout,
  setLayout,
  fontSize,
  setFontSize,
  lineHeight,
  setLineHeight,
  fontFamily,
  setFontFamily,
  margin,
  setMargin,
  bookModeEnabled,
  setBookModeEnabled,
  currentTheme,
  setTheme,
  focusModeEnabled,
  setFocusModeEnabled,
  focusLineCount,
  setFocusLineCount,
  rubyDisplay,
  setRubyDisplay,
  tateChuYokoEnabled,
  setTateChuYokoEnabled,
  isDark,
  emphasisDotsStyle,
  setEmphasisDotsStyle,
}) => {
  const applyHorizontalPreset = () => {
    setLayout('horizontal');
    setFontSize(16);
    setLineHeight('relaxed');
    setFontFamily('sans');
    setMargin('normal');
  };

  const applyVerticalPreset = () => {
    setLayout('vertical');
    setFontSize(18);
    setLineHeight('loose');
    setFontFamily('serif');
    setMargin('normal');
  };

  const activeBtnBg = isDark
    ? 'bg-neutral-700 text-[#FAF8F5]'
    : 'bg-[#FFFFFF] text-[#111111] shadow-xs border border-[#EAEAEA]';
  const inactiveBtnBg = isDark
    ? 'bg-[#1C1C1E] text-[#8E8E93] hover:text-[#FAF8F5]'
    : 'bg-[#F3F3F2] text-[#8E8E93] hover:text-[#111111]';
  const cardBg = isDark ? 'bg-[#1C1C1E]' : 'bg-[#F3F3F2]';
  const borderCol = isDark ? 'border-transparent' : 'border-[#EAEAEA]';

  const themes: { id: NovelTheme; name: string; bg: string; text: string }[] = [
    { id: 'paper', name: '生成り', bg: '#FAF8F5', text: '#2C2C2C' },
    { id: 'sepia', name: 'セピア', bg: '#F4ECD8', text: '#4A3D33' },
    { id: 'night', name: '夜', bg: '#2C2C2C', text: '#E0E0E0' },
    { id: 'midnight', name: '極夜', bg: '#111111', text: '#CCCCCC' },
    { id: 'sakura', name: '桜', bg: '#FDF6F8', text: '#4A3B42' },
    {
      id: 'auto',
      name: '自動',
      bg: 'linear-gradient(135deg, #FAF8F5 50%, #111111 50%)',
      text: '#2C2C2C',
    },
  ];

  const lineHeights: { id: LineHeightPreset; label: string }[] = [
    { id: 'tight', label: '狭い' },
    { id: 'normal', label: '標準' },
    { id: 'relaxed', label: 'やや広い' },
    { id: 'loose', label: '広い' },
  ];

  const marginPresets: { id: MarginPreset; label: string }[] = [
    { id: 'narrow', label: '狭い' },
    { id: 'normal', label: '標準' },
    { id: 'wide', label: '広い' },
  ];

  return (
    <>
      <fieldset className="min-w-0 space-y-1.5 border-0 p-0">
        <legend className="text-[10px] font-bold tracking-widest uppercase opacity-50">
          ページ表示
        </legend>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setBookModeEnabled(false)}
            aria-pressed={!bookModeEnabled}
            className={`min-h-11 rounded-md text-xs font-bold transition-all ${
              !bookModeEnabled ? activeBtnBg : inactiveBtnBg
            }`}
          >
            スクロール
          </button>
          <button
            type="button"
            onClick={() => {
              setBookModeEnabled(true);
              setLayout('vertical');
            }}
            aria-pressed={bookModeEnabled}
            className={`min-h-11 rounded-md text-xs font-bold transition-all ${
              bookModeEnabled ? activeBtnBg : inactiveBtnBg
            }`}
          >
            文庫ページ
          </button>
        </div>
        <p className="text-[10px] opacity-55">
          縦書き一画面一ページ。左右スワイプで紙をめくります。
        </p>
      </fieldset>
      {/* 1. レイアウト方向 (縦書き / 横書き) */}
      <div className="space-y-1.5">
        <span className={'text-[10px] font-bold tracking-widest uppercase opacity-50'}>組方向</span>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => setLayout('vertical')}
            data-testid="layout-vertical-btn"
            className={`flex-1 min-h-11 rounded-md text-xs font-bold flex items-center justify-center space-x-1.5 transition-all ${
              layout === 'vertical' ? activeBtnBg : inactiveBtnBg
            }`}
          >
            <AlignRightIcon size={14} strokeWidth={2} className="rotate-90" />
            <span>縦書き（標準）</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setBookModeEnabled(false);
              setLayout('horizontal');
            }}
            data-testid="layout-horizontal-btn"
            className={`flex-1 min-h-11 rounded-md text-xs font-bold flex items-center justify-center space-x-1.5 transition-all ${
              layout === 'horizontal' ? activeBtnBg : inactiveBtnBg
            }`}
          >
            <AlignJustifyIcon size={14} strokeWidth={2} />
            <span>横書き</span>
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={applyHorizontalPreset}
            className={`min-h-11 rounded-md text-xs font-bold transition-all active:scale-[0.99] ${
              isDark
                ? 'bg-[#1C1C1E] text-neutral-300 hover:text-neutral-100'
                : 'bg-[#F3F3F2] text-neutral-600 hover:text-neutral-800'
            }`}
          >
            横書き最適
          </button>
          <button
            type="button"
            onClick={applyVerticalPreset}
            className={`min-h-11 rounded-md text-xs font-bold transition-all active:scale-[0.99] ${
              isDark
                ? 'bg-[#1C1C1E] text-neutral-300 hover:text-neutral-100'
                : 'bg-[#F3F3F2] text-neutral-600 hover:text-neutral-800'
            }`}
          >
            縦書き標準
          </button>
        </div>
      </div>

      {/* 2. 文字サイズ設定 */}
      <div className="space-y-1.5">
        <div className="flex justify-between items-center">
          <span className={'text-[10px] font-bold tracking-widest uppercase opacity-50'}>
            文字サイズ
          </span>
          <span className="text-[11px] font-mono font-bold">{fontSize}px</span>
        </div>
        <div
          className={`flex items-center justify-between gap-3 p-2.5 rounded-md ${cardBg} border ${borderCol}`}
        >
          <button
            type="button"
            onClick={() => setFontSize(Math.max(14, fontSize - 2))}
            className={`w-11 min-w-11 h-11 rounded text-xs font-bold active:scale-95 transition-all disabled:opacity-40 ${
              isDark ? 'bg-neutral-800 text-stone-300' : 'bg-neutral-200 text-stone-800'
            }`}
            disabled={fontSize <= 14}
          >
            A-
          </button>
          <div className="flex min-w-0 flex-1 items-center space-x-2 py-2">
            <TypeIcon size={12} className="opacity-40" />
            <input
              type="range"
              min="14"
              max="30"
              step="2"
              value={fontSize}
              onChange={(e) => setFontSize(Number(e.target.value))}
              className="min-h-11 w-full min-w-[150px] flex-1 accent-neutral-850 dark:accent-neutral-200"
            />
            <TypeIcon size={16} className="opacity-60" />
          </div>
          <button
            type="button"
            onClick={() => setFontSize(Math.min(30, fontSize + 2))}
            className={`w-11 min-w-11 h-11 rounded text-xs font-bold active:scale-95 transition-all disabled:opacity-40 ${
              isDark ? 'bg-neutral-800 text-stone-300' : 'bg-neutral-200 text-stone-800'
            }`}
            disabled={fontSize >= 30}
          >
            A+
          </button>
        </div>
      </div>

      {/* 3. テーマカラー */}
      <div className="space-y-1.5">
        <span className={'text-[10px] font-bold tracking-widest uppercase opacity-50'}>背景色</span>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
          {themes.map((t) => {
            const isActive = currentTheme === t.id;
            return (
              <button
                type="button"
                key={t.id}
                onClick={() => setTheme(t.id)}
                className={`min-h-11 flex flex-col items-center justify-center py-2 rounded-md border transition-all ${t.bg} ${
                  isActive
                    ? isDark
                      ? 'border-neutral-200 ring-2 ring-neutral-200/10'
                      : 'border-neutral-850 ring-2 ring-neutral-800/10'
                    : isDark
                      ? 'border-[#1F242E]'
                      : 'border-[#EAEAEA]'
                }`}
              >
                <div
                  className="w-3.5 h-3.5 rounded-full flex items-center justify-center border border-black/10"
                  style={{ background: t.bg }}
                >
                  {isActive && (
                    <CheckIcon
                      size={9}
                      strokeWidth={2.5}
                      className={
                        ['paper', 'sepia', 'sakura'].includes(t.id) ||
                        (t.id === 'auto' &&
                          typeof window !== 'undefined' &&
                          !window.matchMedia('(prefers-color-scheme: dark)').matches)
                          ? 'text-neutral-800'
                          : 'text-neutral-100'
                      }
                    />
                  )}
                </div>
                <span className="text-[9px] mt-1 font-serif font-bold" style={{ color: t.text }}>
                  {t.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 4. 行間 & 書体 */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <span className={'text-[10px] font-bold tracking-widest uppercase opacity-50'}>行間</span>
          <select
            value={lineHeight}
            onChange={(e) => setLineHeight(e.target.value as LineHeightPreset)}
            className={`w-full min-h-11 px-3 rounded-md text-xs font-bold focus:outline-none transition-all ${
              isDark
                ? 'bg-[#1C1C1E] border-[#2C2C2E] text-[#FAF8F5]'
                : 'bg-[#F9F9F8] border-[#EAEAEA] text-[#111111]'
            } border`}
          >
            {lineHeights.map((lh) => (
              <option key={lh.id} value={lh.id}>
                {lh.label}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <span className={'text-[10px] font-bold tracking-widest uppercase opacity-50'}>書体</span>
          <div
            className={`flex p-0.5 rounded-md border ${borderCol} ${isDark ? 'bg-neutral-850' : 'bg-neutral-100/50'}`}
          >
            <button
              type="button"
              onClick={() => setFontFamily('serif')}
              className={`flex-1 min-h-11 rounded-sm text-xs font-serif font-bold transition-all ${
                fontFamily === 'serif'
                  ? isDark
                    ? 'bg-neutral-700 text-[#FAF8F5]'
                    : 'bg-[#FFFFFF] text-[#111111] shadow-xs border border-[#EAEAEA]'
                  : 'text-neutral-400 dark:text-neutral-500'
              }`}
            >
              明朝体
            </button>
            <button
              type="button"
              onClick={() => setFontFamily('sans')}
              className={`flex-1 min-h-11 rounded-sm text-xs font-sans font-bold transition-all ${
                fontFamily === 'sans'
                  ? isDark
                    ? 'bg-neutral-700 text-[#FAF8F5]'
                    : 'bg-[#FFFFFF] text-[#111111] shadow-xs border border-[#EAEAEA]'
                  : 'text-neutral-400 dark:text-neutral-500'
              }`}
            >
              ゴシック
            </button>
          </div>
        </div>
      </div>

      {/* 5. 左右の余白 */}
      <div className="space-y-1.5">
        <span className={'text-[10px] font-bold tracking-widest uppercase opacity-50'}>
          画面余白
        </span>
        <div
          className={`flex p-0.5 rounded-md border ${borderCol} ${isDark ? 'bg-neutral-850' : 'bg-neutral-100/50'}`}
        >
          {marginPresets.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setMargin(m.id)}
              className={`flex-1 min-h-11 rounded-sm text-xs font-bold transition-all ${
                margin === m.id
                  ? isDark
                    ? 'bg-neutral-700 text-[#FAF8F5]'
                    : 'bg-[#FFFFFF] text-[#111111] shadow-xs border border-[#EAEAEA]'
                  : 'text-neutral-400 dark:text-neutral-500'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* 6. ルビ（ふりがな）設定 */}
      <div className="space-y-1.5">
        <span className={'text-[10px] font-bold tracking-widest uppercase opacity-50'}>
          ルビ（ふりがな）表示
        </span>
        <div
          className={`flex p-0.5 rounded-md border ${borderCol} ${isDark ? 'bg-neutral-850' : 'bg-neutral-100/50'}`}
        >
          {[
            { id: 'show', label: '通常' },
            { id: 'dim', label: '薄く表示' },
            { id: 'hide', label: '非表示' },
          ].map((rb) => (
            <button
              key={rb.id}
              type="button"
              onClick={() => setRubyDisplay(rb.id as RubyDisplayType)}
              className={`flex-1 min-h-11 rounded-sm text-xs font-bold transition-all ${
                rubyDisplay === rb.id
                  ? isDark
                    ? 'bg-neutral-700 text-[#FAF8F5]'
                    : 'bg-[#FFFFFF] text-[#111111] shadow-xs border border-[#EAEAEA]'
                  : 'text-neutral-400 dark:text-neutral-500'
              }`}
            >
              {rb.label}
            </button>
          ))}
        </div>
      </div>

      {/* 7. 傍点（しるし）設定 */}
      <div className="space-y-1.5">
        <span className={'text-[10px] font-bold tracking-widest uppercase opacity-50'}>
          傍点（しるし）の表示
        </span>
        <div
          className={`flex p-0.5 rounded-md border ${borderCol} ${isDark ? 'bg-neutral-850' : 'bg-neutral-100/50'}`}
        >
          {[
            { id: 'filled-sesame', label: 'ゴマ' },
            { id: 'filled-circle', label: '黒丸' },
            { id: 'open-circle', label: '白丸' },
            { id: 'filled-triangle', label: '三角' },
            { id: 'none', label: 'なし' },
          ].map((ds) => (
            <button
              key={ds.id}
              type="button"
              onClick={() => setEmphasisDotsStyle(ds.id as EmphasisDotsStyleType)}
              className={`flex-1 min-h-11 rounded-sm text-xs font-bold transition-all ${
                emphasisDotsStyle === ds.id
                  ? isDark
                    ? 'bg-neutral-700 text-[#FAF8F5]'
                    : 'bg-[#FFFFFF] text-[#111111] shadow-xs border border-[#EAEAEA]'
                  : 'text-neutral-400 dark:text-neutral-500'
              }`}
            >
              {ds.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};
